import { useCallback, useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import moment from "moment"
import toast from "react-hot-toast"
import axios from "axios"
import useSWR from "swr"

import { Pagination } from "components"
import Table from "components/table"
import Label from "components/forms/label"
import Input from "components/forms/input"
import SelectSearchable from "components/forms/select-searchable"

import useProductUnitRepositories from "repositories/product-unit"
import useStockTransactionRepositories from "repositories/stock-transaction"

import { getDefaultOutletFilterValue, resolveOutletFilter } from "helpers/general-helper"
import OutletFilterSelect from "components/forms/outlet-filter-select"

const productUnitByIdFetcher = (url) => axios.get(url).then((res) => res.data?.data)

const formatNumber = (value) => Number(value ?? 0).toLocaleString("id-ID")

export default function List() {
  const [searchParams, setSearchParams] = useSearchParams()

  const page = searchParams.get("page") || "1"
  // Nilai filter outlet: sentinel grup (GRP_ALL/GRP_HO) atau id outlet tunggal.
  // Default "ikut outlet user"; di-resolve ke CSV id utk endpoint outlet_ids.
  const outletFilter = searchParams.get("outlet_ids") ?? getDefaultOutletFilterValue()
  const { outlet_id_csv: resolvedOutletCsv } = resolveOutletFilter(outletFilter)
  const productUnitId = searchParams.get("product_unit_id") || ""
  const dateFrom = searchParams.get("date_from") || ""
  const dateTo = searchParams.get("date_to") || ""

  const [productSearch, setProductSearch] = useState("")
  const [debouncedProductSearch, setDebouncedProductSearch] = useState("")
  const [selectedProductSnapshot, setSelectedProductSnapshot] = useState(null)
  const [selectedProductCache, setSelectedProductCache] = useState({})

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedProductSearch(productSearch.trim())
    }, 400)

    return () => clearTimeout(timeoutId)
  }, [productSearch])

  const productParams = useMemo(
    () => ({
      paginate: true,
      search: debouncedProductSearch || undefined,
    }),
    [debouncedProductSearch]
  )

  const { data: productSelectionRaw, isLoading: isLoadingProductSelection } =
    useProductUnitRepositories(productParams)

  const productOptions = useMemo(() => {
    if (Array.isArray(productSelectionRaw)) return productSelectionRaw
    if (Array.isArray(productSelectionRaw?.data)) return productSelectionRaw.data
    return []
  }, [productSelectionRaw])

  const productSelection = useMemo(() => {
    const currentSelected =
      (selectedProductSnapshot &&
      String(selectedProductSnapshot?.id || "") === String(productUnitId || "")
        ? selectedProductSnapshot
        : null) ||
      selectedProductCache[String(productUnitId || "")]

    if (!currentSelected) return productOptions

    const exists = productOptions.some(
      (row) => String(row?.id || "") === String(currentSelected?.id || "")
    )

    return exists ? productOptions : [currentSelected, ...productOptions]
  }, [productOptions, selectedProductCache, selectedProductSnapshot, productUnitId])

  useEffect(() => {
    if (!productUnitId) {
      setSelectedProductSnapshot(null)
      return
    }

    if (
      selectedProductSnapshot &&
      String(selectedProductSnapshot?.id || "") === String(productUnitId)
    ) {
      return
    }

    const cached = selectedProductCache[String(productUnitId)]
    if (cached) {
      setSelectedProductSnapshot(cached)
      return
    }

    const found = productOptions.find(
      (row) => String(row.id) === String(productUnitId)
    )

    if (found) {
      setSelectedProductSnapshot(found)
      setSelectedProductCache((prev) => ({
        ...prev,
        [String(found.id)]: found,
      }))
    }
  }, [productOptions, productUnitId, selectedProductCache, selectedProductSnapshot])

  const stockParams = useMemo(
    () => ({
      page,
      outlet_ids: resolvedOutletCsv || undefined,
      product_unit_id: productUnitId || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      order_by: "transaction_date",
      direction: "desc",
      enabled: Boolean(productUnitId),
    }),
    [page, resolvedOutletCsv, productUnitId, dateFrom, dateTo]
  )

  const { data: stockDataRaw, isLoading, isValidating } = useStockTransactionRepositories(stockParams)

  const cachedSelected = productUnitId
    ? (selectedProductSnapshot && String(selectedProductSnapshot.id || "") === String(productUnitId)
        ? selectedProductSnapshot
        : selectedProductCache[String(productUnitId)] || null)
    : null

  const shouldFetchProductDetail = Boolean(productUnitId) && !cachedSelected

  const { data: productDetailRaw } = useSWR(
    shouldFetchProductDetail
      ? `${process.env.REACT_APP_INVENTORY_API_URL}/product/unit/${encodeURIComponent(productUnitId)}`
      : null,
    productUnitByIdFetcher
  )

  const fetchedProductDetail = useMemo(() => {
    if (!productDetailRaw) return null
    if (String(productDetailRaw?.id || "") !== String(productUnitId || "")) return null
    return productDetailRaw
  }, [productDetailRaw, productUnitId])
  const stockData = useMemo(() => stockDataRaw ?? {}, [stockDataRaw])
  const rows = useMemo(() => stockData?.data ?? [], [stockData])
  const isHistoryLoading = Boolean(productUnitId) && (isLoading || isValidating)

  const mergeParams = useCallback((next) => {
    setSearchParams((prev) => {
      const current = Object.fromEntries(prev.entries())
      const merged = { ...current, ...next }
      Object.keys(merged).forEach((key) => {
        if (merged[key] === null || merged[key] === undefined || merged[key] === "") {
          delete merged[key]
        }
      })

      const currentSerialized = new URLSearchParams(current).toString()
      const mergedSerialized = new URLSearchParams(merged).toString()

      if (currentSerialized === mergedSerialized) {
        return prev
      }

      return new URLSearchParams(merged)
    })
  }, [setSearchParams])

  const handleExport = () => {
    if (!productUnitId) {
      toast.error("Pilih product unit terlebih dahulu")
      return
    }

    const params = new URLSearchParams({
      report_type: "stock_card",
      product_unit_id: productUnitId,
      ...(resolvedOutletCsv ? { outlet_ids: resolvedOutletCsv } : {}),
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo ? { date_to: dateTo } : {}),
    })

    window.open(`${process.env.REACT_APP_INVENTORY_API_URL}/stock/transfer/export?${params.toString()}`, "_blank")
  }

  const summary = useMemo(() => {
    const serverSummary = stockData?.summary
    if (serverSummary) {
      return {
        totalIn: Number(serverSummary.total_in ?? 0),
        totalOut: Number(serverSummary.total_out ?? 0),
        latestBalance: Number(serverSummary.latest_balance ?? 0),
      }
    }

    return rows.reduce(
      (acc, row) => {
        const qty = Number(row.quantity ?? 0)
        if (qty > 0) acc.totalIn += qty
        if (qty < 0) acc.totalOut += Math.abs(qty)
        acc.latestBalance = Number(row.balance ?? row.current_stock ?? acc.latestBalance)
        return acc
      },
      { totalIn: 0, totalOut: 0, latestBalance: 0 }
    )
  }, [rows, stockData?.summary])

    const resolvedSelectedProduct = useMemo(() => {
    const fromSnapshot =
      (selectedProductSnapshot &&
      String(selectedProductSnapshot?.id || "") === String(productUnitId || "")
        ? selectedProductSnapshot
        : null) ||
      selectedProductCache[String(productUnitId || "")] ||
      productOptions.find((row) => String(row?.id || "") === String(productUnitId || ""))

    if (fromSnapshot) return fromSnapshot

    if (fetchedProductDetail && String(fetchedProductDetail?.id || "") === String(productUnitId || "")) {
      return {
        id: fetchedProductDetail.id,
        name: fetchedProductDetail.name,
        long_name: fetchedProductDetail.long_name || fetchedProductDetail.name,
        sku: fetchedProductDetail.sku,
        product_model: fetchedProductDetail.product_model,
      }
    }

    const firstRow = rows?.[0]

    if (firstRow && String(firstRow?.product_unit_id || "") === String(productUnitId || "")) {
      const productUnitField = firstRow?.product_unit
      const nameFromObject = productUnitField && typeof productUnitField === "object"
        ? (productUnitField.long_name || productUnitField.name)
        : null
      const nameFromString = typeof productUnitField === "string" ? productUnitField : null

      const resolvedName =
        nameFromObject ||
        nameFromString ||
        firstRow?.product_unit_long_name ||
        firstRow?.product_unit_name ||
        firstRow?.product_name ||
        firstRow?.item_name ||
        null

      const resolvedSku =
        (productUnitField && typeof productUnitField === "object" ? productUnitField.sku : null) ||
        firstRow?.product_unit_sku ||
        firstRow?.sku ||
        null

      if (resolvedName || resolvedSku) {
        return {
          id: firstRow.product_unit_id,
          name: resolvedName,
          long_name: resolvedName,
          sku: resolvedSku,
        }
      }
    }

    return null
  }, [
    productOptions,
    productUnitId,
    rows,
    selectedProductCache,
    selectedProductSnapshot,
    fetchedProductDetail,
  ])

    useEffect(() => {
    if (!resolvedSelectedProduct?.id) return
    if (String(resolvedSelectedProduct.id) !== String(productUnitId || "")) return

    setSelectedProductSnapshot((prev) => {
      if (
        prev &&
        String(prev?.id || "") === String(resolvedSelectedProduct.id || "") &&
        (prev?.long_name || prev?.name || prev?.sku) ===
          (resolvedSelectedProduct?.long_name ||
            resolvedSelectedProduct?.name ||
            resolvedSelectedProduct?.sku)
      ) {
        return prev
      }

      return resolvedSelectedProduct
    })

    setSelectedProductCache((prev) => {
      const key = String(resolvedSelectedProduct.id)
      const existing = prev[key]

      if (
        existing &&
        (existing?.long_name || existing?.name || existing?.sku) ===
          (resolvedSelectedProduct?.long_name ||
            resolvedSelectedProduct?.name ||
            resolvedSelectedProduct?.sku)
      ) {
        return prev
      }

      return {
        ...prev,
        [key]: {
          ...(existing || {}),
          ...resolvedSelectedProduct,
        },
      }
    })
  }, [resolvedSelectedProduct, productUnitId])

  const dataListHeaders = [
    { isAction: false, content: "Tanggal Transaksi" },
    { isAction: false, content: "Referensi" },
    { isAction: false, content: "Tipe" },
    { isAction: false, content: "Qty Masuk" },
    { isAction: false, content: "Qty Keluar" },
    { isAction: false, content: "Saldo" },
    { isAction: false, content: "Dibuat Oleh" },
  ]

  const dataListColumns = [
    {
      type: "default",
      bold: true,
      content: (v) => v.transaction_date ? moment(v.transaction_date).format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      type: "default",
      content: (v) => (
        <div className="flex flex-col">
          <span className="font-medium text-neutral-800">{v.reference_id || "-"}</span>
          <span className="text-xs text-neutral-500">{v.outlet?.name || "-"}</span>
        </div>
      ),
    },
    { type: "default", content: (v) => <span className="px-2 py-1 text-xs rounded bg-neutral-100">{v.reference_type || "-"}</span> },
    {
      type: "default",
      content: (v) => {
        const qty = Number(v.quantity ?? 0)
        return qty > 0 ? <span className="font-semibold text-green-700">{formatNumber(qty)}</span> : "-"
      },
    },
    {
      type: "default",
      content: (v) => {
        const qty = Number(v.quantity ?? 0)
        return qty < 0 ? <span className="font-semibold text-red-700">{formatNumber(Math.abs(qty))}</span> : "-"
      },
    },
    { type: "default", content: (v) => <span className="font-semibold text-neutral-800">{formatNumber(v.balance ?? v.current_stock)}</span> },
    { type: "default", content: (v) => v.created_by_name || v.created_by || "-" },
  ]

  return (
    <div className="space-y-6">
      <div className="p-5 bg-white border border-neutral-200 rounded-2xl">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">Kartu Stock</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Histori mutasi stok per product unit dengan saldo berjalan.
            </p>
          </div>

          <button
            type="button"
            onClick={handleExport}
            className="inline-flex items-center px-4 py-3 text-sm font-medium text-white transition bg-emerald-600 rounded-xl"
          >
            Ekspor Excel
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 mt-5 lg:grid-cols-[240px_minmax(0,1fr)_180px_180px]">
          <div>
            <Label value="Outlet" />
            <OutletFilterSelect
              value={outletFilter}
              onChange={(v) => mergeParams({ outlet_ids: v || null, page: 1 })}
              className="block w-full px-2 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-50 disabled:opacity-75"
              title="Outlet"
            />
          </div>

          <div>
            <Label value="Product Unit" />
            <SelectSearchable
              selection={productSelection}
              isLoading={isLoadingProductSelection}
              value={productUnitId}
              onChange={(value, row) => {
                const nextValue = String(value || "")
                if (!nextValue) return
                if (nextValue === String(productUnitId || "")) return

                const stableSelectedRow =
                  row ||
                  productOptions.find((item) => String(item?.id || "") === nextValue) ||
                  selectedProductCache[nextValue] ||
                  (selectedProductSnapshot &&
                  String(selectedProductSnapshot?.id || "") === nextValue
                    ? selectedProductSnapshot
                    : null)

                if (stableSelectedRow) {
                  setSelectedProductSnapshot(stableSelectedRow)
                  setSelectedProductCache((prev) => ({
                    ...prev,
                    [nextValue]: stableSelectedRow,
                  }))
                }

                setProductSearch("")

                mergeParams({
                  product_unit_id: nextValue,
                  page: 1,
                })
              }}
              keyValue={(v) => v.id}
              title={(v) => v.long_name || v.name}
              description={(v) => [v.sku, v.product_model?.name].filter(Boolean).join(" · ")}
              placeholder="Pilih Product Unit"
              onSearchChange={(value) => setProductSearch(String(value || ""))}
              serverSearch
              clearOnClose={false}
            />
          </div>

          <div>
            <Label value="Tanggal Dari" />
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => mergeParams({ date_from: e.target.value || null, page: 1 })}
            />
          </div>

          <div>
            <Label value="Tanggal Sampai" />
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => mergeParams({ date_to: e.target.value || null, page: 1 })}
            />
          </div>
        </div>
      </div>

      {!productUnitId ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white border border-neutral-200 rounded-2xl text-center">
          <p className="text-base font-semibold text-neutral-800">Pilih product unit</p>
          <p className="mt-1 text-sm text-neutral-500">
            Kartu stock akan tampil setelah product unit dipilih. Kosongkan outlet untuk menampilkan semua outlet.
          </p>
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="p-4 bg-white border border-neutral-200 rounded-2xl md:col-span-2">
              <div className="text-xs font-semibold tracking-wide text-neutral-500">Produk</div>
              <div className="mt-2 text-lg font-semibold text-neutral-900">
                {resolvedSelectedProduct?.long_name || resolvedSelectedProduct?.name || resolvedSelectedProduct?.sku || productUnitId}
              </div>
              <div className="mt-1 text-sm text-neutral-500">
                {isHistoryLoading ? "Memuat kartu stock..." : (resolvedSelectedProduct?.sku || "-")}
              </div>
            </div>
            <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
              <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Masuk</div>
              <div className="mt-2 text-2xl font-semibold text-green-700">
                {isHistoryLoading ? "..." : formatNumber(summary.totalIn)}
              </div>
            </div>
            <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
              <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Keluar</div>
              <div className="mt-2 text-2xl font-semibold text-red-700">
                {isHistoryLoading ? "..." : formatNumber(summary.totalOut)}
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border border-neutral-200 rounded-2xl">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">Histori Mutasi</h3>
                <p className="mt-1 text-sm text-neutral-500">
                  {isHistoryLoading ? (
                    <span className="font-medium text-neutral-800">Memuat histori kartu stock...</span>
                  ) : (
                    <>Saldo terbaru: <span className="font-medium text-neutral-800">{formatNumber(summary.latestBalance)}</span></>
                  )}
                </p>
              </div>

              {isHistoryLoading && (
                <div className="inline-flex items-center gap-2 px-3 py-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-xl">
                  <span className="inline-block w-2.5 h-2.5 bg-blue-600 rounded-full animate-pulse" />
                  <span>Memuat histori product...</span>
                </div>
              )}
            </div>

            <div className="mt-5">
              <Table
                isLoading={isHistoryLoading}
                data={rows}
                headers={dataListHeaders}
                columns={dataListColumns}
                key={(v) => `${v.reference_id}-${v.product_unit_id}-${v.transaction_date}`}
                isEmpty={!rows.length}
                isEmptyOnSearching={false}
              />
            </div>

            <div className="mt-5">
              <Pagination links={stockData?.links} from={stockData?.from} to={stockData?.to} total={stockData?.total} />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
